import {
  DEFAULT_CARD_DECK,
  GAME_CATEGORY,
  isGameId,
  type GameId,
  type StartIntent,
} from "@bing/shared";
import { shareURL } from "@telegram-apps/sdk";
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import logoUrl from "../assets/logo.jpg";
import { Avatar } from "../components/Avatar";
import { GameIcon } from "../components/GameIcon";
import { InviteFriendList } from "../components/InviteFriendList";
import { Sheet } from "../components/social-ui";
import { GAMES } from "../config";
import { CARD_DECKS } from "../game/card-decks";
import { armOf } from "@bing/engine-mensch";
import { armColor } from "../game/mensch-board";
import { tableSeatAngle } from "../game/table-seat-layout";
import type { LobbyPlayer, LobbyState } from "../game/online-connection";
import { inviteLink, type TableSettings } from "../game/private-table";
import { presetAvatarFor } from "../game/seat-avatar";
import type { SeatAvatar } from "../game/seat-identity";
import { shareTableInvite } from "../game/table-share";
import { useRosterSfx } from "../game/use-table-sfx";
import { faDigits } from "../i18n/digits";
import { fa } from "../i18n/fa";
import { playSfx } from "../sound/sfx";
import { useSession } from "../store/context";

/* ------------------------------------------------------------------ *
 * Online matchmaking / private-table waiting room — a faithful port of the
 * Stitch "Waiting Room" design (docs/stitch/16-waiting-room.html): a brand
 * header, a round felt table with seats pinned around the rim (filled avatars
 * vs. dashed «منتظر…» placeholders), a floating center card carrying the
 * players `x/y` count + the host's «شروع با ربات» start, and a bottom sheet
 * with the copyable invite link + a gold «ارسال دعوت‌نامه» share button.
 *
 * All data/behaviour is preserved — only presentation moves to Tailwind:
 *  - seat list (filled vs empty, host 👑 / bot markers),
 *  - the players count + the starting countdown,
 *  - host-only start / start-with-bot,
 *  - the private-vs-general (quick-match) distinction,
 *  - the invite link + copy + share actions — on a private table for EVERY seated
 *    player, not only the host (card-47; see {@link InvitePanel}): inline in the host's
 *    sheet, behind one «دعوت دوستان» button in a guest's (see {@link GuestInvite}).
 * ------------------------------------------------------------------ */

/** Screen-local Persian strings the shared `fa` dictionary doesn't cover yet
 * (mirrors the `Friends` redesign — we don't edit the central i18n file here).
 * Friendly-casual register (خودمونی): informal 2nd-person, no «شما»/«کنید». */
const t = {
  waitingForPlayers: "منتظر بقیه هستیم",
  startWithBot: "با ربات‌ها شروع کن",
  inviteFriends: "دوستات رو به میز دعوت کن",
  sendInvite: "دعوت دوستان از تلگرام",
  /** The copy-room-link BUTTON label (replaces the old always-visible URL field). */
  copyLinkLabel: "کپی لینک میز",
  copied: "کپی شد",
  /** The message that rides Telegram's native share sheet next to the link. */
  shareText: (game: string) => `بیا با هم «${game}» بازی کنیم! 🎴`,
  waitingForTable: "منتظریم میز پر شه…",
  fillingNote: "اگه میز پر نشه، با ربات‌ها شروع می‌کنیم",
  noHost: "بدون میزبان",
  // Team-selection stage (partnership games; the host arranges who partners with whom
  // — including when the empty chairs are about to be filled by bots).
  teamsTitle: "چیدن تیم‌ها",
  teamsHint: "روی دو نفر بزن تا جاشون عوض بشه — یار تو کسیه که روبه‌روت می‌شینه",
  teamsWaitingForHost: "میزبان در حال چیدن تیم‌هاست…",
  teamsBotSeat: "ربات",
  team1: "تیم ۱",
  team2: "تیم ۲",
  teamsConfirm: "تأیید و شروع بازی",
  // Full-house celebration — every seat filled by a HUMAN and the start countdown
  // is running (the group-invite "table complete" moment: friends fill the table
  // from a Telegram link).
  // ITEM 9 — منچ's arrangement stage: colours, not partnerships.
  colorsTitle: "رنگ بازیکن‌ها",
  colorsHint: "روی دو بازیکن بزن تا رنگشان جابه‌جا شود، بعد تأیید کن.",
  colorsWaitingForHost: "میزبان در حال تعیین رنگ‌هاست…",
  fullHouseTitle: "🎉 بازیکن‌ها تکمیل شدند!",
  fullHouseSubtitle: "بازی تا چند لحظهٔ دیگر شروع می‌شود…",
} as const;

/**
 * The HOST's visible countdown to an un-started private table's auto-close (`idleDeadline`, epoch
 * ms). Ticks locally against `Date.now()` (mm:ss, Persian digits); turns urgent under a minute. The
 * table won't close if the host finalizes «یار و حریف» / starts before it reaches 0.
 */
function IdleCountdown({ deadline }: { deadline: number }): ReactNode {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  const remaining = Math.max(0, Math.ceil((deadline - now) / 1000));
  const mm = Math.floor(remaining / 60);
  const ss = remaining % 60;
  const urgent = remaining <= 60;
  return (
    <span
      title={fa.online.idleCloseHint}
      className={`flex flex-none items-center gap-1 rounded-full border px-2 py-1 text-[11px] font-bold tabular-nums ${
        urgent
          ? "border-danger-deep/50 bg-danger-deep/10 text-danger-deep"
          : "border-gold-deep/40 bg-surface-container text-gold-deep"
      }`}
    >
      <Icon name="timer" size={14} />
      {faDigits(mm)}:{faDigits(ss).padStart(2, "۰")}
    </span>
  );
}

/* ── Inline Material-Symbol SVGs (no icon font; each glyph is one `currentColor`
 *    path, mirroring the Stitch design's `material-symbols-outlined`). ── */
type IconName =
  | "personAdd"
  | "robot"
  | "copy"
  | "check"
  | "share"
  | "timer"
  | "sound"
  | "chat"
  | "deck"
  | "modes"
  | "hands";
const ICON_PATH: Record<IconName, string> = {
  sound:
    "M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z",
  chat: "M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z",
  // A SPADE pip — the suit-less calls' own emblem here (سرس is the Ace-high one, and the picker
  // draws the three modes in their own box beside the four خال).
  modes:
    "M12 2C9 6 4 8.5 4 13a4 4 0 0 0 6.8 2.9c-.2 1.9-1 3.4-2.3 4.1v.5h7v-.5c-1.3-.7-2.1-2.2-2.3-4.1A4 4 0 0 0 20 13c0-4.5-5-7-8-11z",
  deck: "M2.53 19.65l1.34.56v-9.03l-2.43 5.86c-.41 1.02.08 2.19 1.09 2.61zm19.5-3.7L17.07 3.98c-.31-.75-1.04-1.21-1.81-1.23-.26 0-.53.04-.79.15L7.1 5.95c-.75.31-1.21 1.03-1.23 1.8-.01.27.04.54.15.8l4.96 11.97c.31.76 1.05 1.22 1.83 1.23.26 0 .52-.05.77-.15l7.36-3.05c1.02-.42 1.51-1.59 1.09-2.6zM7.88 8.75c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z",
  personAdd:
    "M15 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm-9-1V8H4v3H1v2h3v3h2v-3h3v-2H6zm9 3c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z",
  robot:
    "M20 9V7c0-1.1-.9-2-2-2h-3c0-1.66-1.34-3-3-3S9 3.34 9 5H6c-1.1 0-2 .9-2 2v2c-1.66 0-3 1.34-3 3s1.34 3 3 3v4c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2v-4c1.66 0 3-1.34 3-3s-1.34-3-3-3zM9 13c-.83 0-1.5-.67-1.5-1.5S8.17 10 9 10s1.5.67 1.5 1.5S9.83 13 9 13zm7 4H8v-2h8v2zm-1-4c-.83 0-1.5-.67-1.5-1.5S14.17 10 15 10s1.5.67 1.5 1.5S15.83 13 15 13z",
  copy: "M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z",
  check: "M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z",
  share:
    "M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z",
  timer:
    "M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z",
  // Stacked discs — HOW LONG THE MATCH IS: دوز's «چند دست», the one thing its host chooses besides
  // the clock, and شلم's «بازی تا چند امتیاز» (never both on one table).
  hands:
    "M12 3C7.58 3 4 4.79 4 7s3.58 4 8 4 8-1.79 8-4-3.58-4-8-4zm0 13c-4.42 0-8-1.79-8-4v3c0 2.21 3.58 4 8 4s8-1.79 8-4v-3c0 2.21-3.58 4-8 4z",
};
function Icon({ name, size = 20 }: { name: IconName; size?: number }): ReactNode {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="currentColor"
      aria-hidden="true"
      style={{ display: "block", flexShrink: 0 }}
    >
      <path d={ICON_PATH[name]} />
    </svg>
  );
}

/** A small spinning ring — the design's "finding players / waiting" affordance. */
function Spinner({ size = 18 }: { size?: number }): ReactNode {
  return (
    <span
      aria-hidden
      className="inline-block animate-spin rounded-full border-2 border-outline-variant"
      style={{
        width: size,
        height: size,
        borderTopColor: "rgb(var(--c-secondary))",
        flex: "0 0 auto",
      }}
    />
  );
}

/** A few fanned card-backs above the local player's seat (the design's flourish). */
/** Seat centre as a percentage point around the felt rim, at `angle` degrees (screen axes: 90 is
 * the bottom, where the local player always sits). The angle is the GAME'S OWN TABLE'S — see
 * {@link tableSeatAngle}: the ring used to go clockwise for every game, and three of them deal
 * their players onto different sides. */
function seatPos(angle: number): { left: string; top: string } {
  const theta = (angle * Math.PI) / 180;
  const r = 40; // percent of the felt radius
  return { left: `${50 + r * Math.cos(theta)}%`, top: `${50 + r * Math.sin(theta)}%` };
}

/**
 * A seat's REAL face in this room: the occupant's uploaded photo, else the preset THEY chose, else
 * the seat's positional stand-in (nobody known sits there — an empty chair is a bot at the deal).
 * ONE resolution for the felt's seats and the team stage's chips: the chips used to draw the
 * positional preset whatever the roster carried, so a host arranging teams saw stand-ins where the
 * felt showed the players' photos (card-67 A3). The roster's `user` is the identity's only source.
 */
function lobbySeatFace(player: LobbyPlayer | undefined, seat: number): SeatAvatar {
  return {
    avatarId: player?.user?.avatarId ?? presetAvatarFor(seat),
    avatarPhotoId: player?.user?.avatarPhotoId ?? null,
    official: player?.user?.isOfficial === true,
  };
}

/** One seat pinned around the felt: a filled avatar (+ gold ring for you, 👑 for
 * the host, «ربات» for a bot) or a dashed «منتظر…» placeholder. */
function Seat({
  seatIndex,
  angle,
  player,
  isYou,
  onInvite,
}: {
  seatIndex: number;
  /** Where this game's TABLE draws the seat, from the viewer's chair — {@link tableSeatAngle}. */
  angle: number;
  player: LobbyPlayer | undefined;
  isYou: boolean;
  /** PUBLIC tables: set on an EMPTY seat any seated player may invite a friend into. */
  onInvite?: (() => void) | undefined;
}): ReactNode {
  const filled = player !== undefined;
  const isHost = player?.isHost ?? false;
  const isBot = player?.isBot ?? false;
  const name = !filled ? fa.online.seatEmpty : isYou ? fa.online.you : player.name;
  const face = lobbySeatFace(player, seatIndex);
  const pos = seatPos(angle);
  // EVERY badge sits at the TOP-RIGHT of its seat — identical on all of them (operator, 2026-08-06;
  // the earlier inward-mirroring made the right-hand seat the odd one out). Measured in Chromium
  // across the full viewport matrix: at top-right the right-hand seat's badge still clears the
  // screen edge on the narrowest phone, so uniformity costs nothing.
  //
  // ⚠️ PHYSICAL `right`, NEVER `justify-end`. That is DIRECTION-relative and this app is
  // `dir="rtl"`, so a flex justification renders on the OPPOSITE side from the one it names —
  // measured: `justify-end` put the badge on the seat's LEFT.
  const badgeSide = "right" as const;

  return (
    <div
      data-seat={seatIndex}
      className="absolute flex w-20 flex-col items-center gap-1 text-center"
      style={{ left: pos.left, top: pos.top, transform: "translate(-50%, -50%)" }}
    >
      <div className="relative">
        {filled ? (
          <span
            // Block-level flex + border-box: an inline-flex ring wrapper picks up
            // the line-box baseline strut on Android and drifts off the avatar
            // circle (see Avatar's ringClass note) — same guard as the seat rings.
            data-seat-part=""
            className="flex shrink-0 rounded-full"
            style={{
              padding: 2,
              boxSizing: "border-box",
              boxShadow: isYou
                ? "0 0 12px 2px rgb(var(--c-secondary) / .35), 0 0 0 2px rgb(var(--c-secondary))"
                : "0 0 0 2px rgb(var(--c-outline-variant))",
            }}
          >
            {/* The seat's REAL face: their uploaded photo, else the preset THEY chose,
                else the positional stand-in. The roster already carries the identity
                (the players panel renders it) — this seat used to hardcode the
                positional preset, so a friend who joined never showed their picture. */}
            <Avatar
              avatarId={face.avatarId}
              avatarPhotoId={face.avatarPhotoId}
              size={44}
              official={face.official}
            />
          </span>
        ) : (
          <span
            aria-hidden
            data-seat-part=""
            className="flex animate-pulse items-center justify-center rounded-full border-2 border-dashed border-outline-variant bg-surface-container-highest text-outline-variant"
            style={{ width: 48, height: 48 }}
          >
            <Icon name="personAdd" size={22} />
          </span>
        )}
        {onInvite ? (
          // PUBLIC tables: invite a friend into this empty chair. Same shape as the mid-game
          // bot-seat «+» (see HokmTable) — a tap-target over the disc carrying a small gold badge.
          //
          // `appearance-none bg-transparent border-0` is LOAD-BEARING: Tailwind preflight is OFF,
          // so without them iOS WebKit paints its native push-button chrome (an opaque light fill)
          // right over the seat — the reported white/blank disc.
          <button
            type="button"
            onClick={onInvite}
            data-invite-seat={seatIndex}
            aria-label={fa.online.seatInvitePublic}
            className="absolute inset-0 appearance-none rounded-full border-0 bg-transparent active:scale-95"
          >
            <span
              data-badge-side={badgeSide}
              data-seat-part=""
              className="absolute flex h-4 w-4 items-center justify-center rounded-full bg-secondary text-[11px] font-black leading-none text-ink-on-gold shadow-md"
              style={{ top: -4, [badgeSide]: -4 }}
            >
              +
            </span>
          </button>
        ) : null}
        {isHost ? (
          <span
            role="img"
            aria-label={fa.online.host}
            title={fa.online.host}
            data-seat-part=""
            className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-secondary text-[11px] shadow"
          >
            👑
          </span>
        ) : null}
      </div>
      <span
        // `dir="auto"`: an RTL block clips the BEGINNING of a Latin name (`## Gotchas`).
        dir="auto"
        data-seat-part=""
        className={`max-w-full truncate text-[11px] font-bold leading-tight ${
          isYou ? "text-secondary" : filled ? "text-on-surface" : "text-on-surface-variant"
        }`}
      >
        {name}
      </span>
      {isBot ? (
        <span data-seat-part="" className="text-[10px] leading-none text-on-surface-variant">
          {fa.online.bot}
        </span>
      ) : null}
    </div>
  );
}

/**
 * Compact read-only summary of the table's rules (shown to EVERYONE in a private
 * lobby) — the host's four owner-set choices (per-turn time / table sound / table
 * chat / card deck) as ONE row of icon+value pills, in the host's sheet and in every
 * guest's. Until card-47 a guest got a labelled 2×2 grid here instead; it gave way to
 * this row when a guest's sheet gained its «دعوت دوستان» button — the grid is 81px
 * (four chips) to 125px (five) against the row's 21px, and a guest's sheet must stay
 * no taller than it was on master (see {@link GuestInvite}) — and the grid, left with
 * no caller, was deleted (card-47 review F9).
 */
function SettingsChips({
  settings,
  game,
}: {
  settings: TableSettings;
  /**
   * WHICH GAME THIS TABLE IS — passed in, never inferred from the settings' shape.
   *
   * ⚠️ THE FIRST CUT INFERRED IT AND WAS WRONG ON حکم, WHICH IS ~90% OF WHAT THIS PLATFORM
   * PLAYS. The heuristic was «a `matchLength` with no حکم keys and no chess/backgammon block must
   * be دوز» — but `normalizeSettings` defaults ONLY `matchLength` for a private حکم table and
   * forces `hokmModes`/`hokmModeKot` on PUBLIC ones, so a private حکم table created by a CACHED
   * older bundle (which sends no booleans) arrives as exactly that shape. It would have replaced
   * حکم's card-deck chip with «۷ دست». The game id is right here on the screen; use it.
   */
  game?: string;
}): ReactNode {
  // CHESS tables carry a `chess` block INSTEAD of the card settings — show the three chess
  // choices (colour / time control / disconnect) read-only, one compact row for both host
  // and guest. A card table never has this block, so the card branch below is untouched.
  const chess = settings.chess;
  if (chess) {
    const colorVal =
      chess.color === "white"
        ? fa.chess.colorWhite
        : chess.color === "black"
          ? fa.chess.colorBlack
          : fa.chess.colorRandom;
    const timeVal = chess.timeControl === "none" ? fa.chess.untimed : faDigits(chess.timeControl);
    const dcVal =
      chess.disconnectPolicy === "claim"
        ? fa.chess.dcClaim
        : chess.disconnectPolicy === "bot"
          ? fa.chess.dcBot
          : fa.chess.dcPause;
    const items: readonly (readonly [string, string])[] = [
      [fa.chess.color, colorVal],
      [fa.chess.timeControl, timeVal],
      [fa.chess.disconnect, dcVal],
    ];
    return (
      <div
        data-testid="table-settings"
        className="flex flex-wrap items-center justify-center gap-1.5"
      >
        {items.map(([label, value]) => (
          <span
            key={label}
            className="flex items-center gap-1 rounded-full border border-outline-variant bg-surface-container-low px-2 py-1 text-[11px] leading-none text-on-surface-variant"
          >
            <span className="whitespace-nowrap">{label}</span>
            <span className="whitespace-nowrap font-bold text-on-surface">{value}</span>
          </span>
        ))}
      </div>
    );
  }

  const deckName = CARD_DECKS[settings.deck ?? DEFAULT_CARD_DECK]?.name ?? "";
  const timerVal = `${faDigits(settings.turnTimerSec)} ${fa.online.seconds}`;
  /*
   * دوز REPLACES THE DECK CHIP WITH ITS HAND COUNT — it does not ADD a fifth.
   *
   * A board game has no card deck, so «طرح کارت‌ها» says nothing about a دوز table; «۳ or ۵ دست»
   * is the one thing that changes what a guest is signing up for, and it is the only choice its
   * host makes besides the clock. Swapping rather than appending keeps the MEASURED four-chip
   * single-line fit (305.1 px for five against a 288 px container at 320) exactly as it was.
   */
  const doozHands = game === "dooz" && typeof settings.matchLength === "number";
  const handsVal = doozHands ? faDigits(settings.matchLength as number) : "";
  /*
   * شلم ADDS ITS MATCH TARGET — a FIFTH chip, never a swap: شلم is a card game, so its deck chip
   * still says something. Shown on BOTH values (unlike the حکم modes chip, which marks only the
   * non-default): ۶۶۰ or ۱۲۰۰ is half an evening either way, and it is what a guest signs up for.
   * Gated on the GAME ID passed in, never on the settings' shape; absent when the server published
   * none (a game-server that predates the setting). Cost, as with the modes chip: the row — the
   * host's and, since card-47, every guest's — wraps at 320 px.
   */
  const shelemTarget =
    game === "shelem" && typeof settings.shelemTarget === "number" ? settings.shelemTarget : null;
  // Sound: MUTED means sound OFF for everyone. Chat: allowed unless explicitly off.
  // Compared against the permissive defaults so a frame that predates these fields
  // (a brief deploy window) reads as unmuted + chat-on, not the scary opposite.
  const soundVal = settings.muted === true ? fa.online.off : fa.online.on;
  const chatVal = settings.chatAllowed === false ? fa.online.off : fa.online.on;

  // A private sheet is short of height (the host's carries the invite prompt + share buttons +
  // friend list; a guest's must stay within master's footprint — see GuestInvite), so the owner
  // settings ride ONE compact line as icon+value pills (a glyph replaces the text label).
  //
  // MEASURED (real Vazirmatn, live DOM) — chip widths timer 62.5 · sound 54.8 · chat 54.8 ·
  // deck 68.5 · modes 78.5, plus four gaps: 305.1px, against a container of 288 / 328 / 361px
  // at 320 / 360 / 393. So it is ONE line from 360 up. At 320 it still wraps and no arrangement
  // of five chips would not: the HOST's sheet is at its 0.46×app-h cap there anyway (the felt stays
  // at its 120px floor whether this row is one line or two), and a GUEST's — rules + waiting line +
  // one button — is still 39px under master's with the second line (MEASURED 134 vs 173px).
  // `flex-wrap` is what makes that degrade gracefully.
  const items: readonly (readonly [IconName, string])[] = [
    ["timer", timerVal],
    ["sound", soundVal],
    ["chat", chatVal],
    doozHands ? (["hands", handsVal] as const) : (["deck", deckName] as const),
    ...(shelemTarget !== null
      ? ([["hands", faDigits(shelemTarget)]] as const satisfies readonly (readonly [
          IconName,
          string,
        ])[])
      : []),
    // حکم's کوت-under-a-mode setting, and ONLY when the host turned it ON. Shown as a
    // FIFTH chip rather than always: OFF is the default and the conventional reading, so a
    // permanent «بدون کوت» pill would be noise on every table — and it would cost the
    // MEASURED single-line fit of the four owner settings at 320px. A non-default scoring
    // rule is the thing everyone at the table needs to see. Absent on every other game:
    // the server strips the key (see TableController.normalizeSettings).
    // حکم's suit-less CALLS, and ONLY when this table offers them. Same rule as the کوت chip
    // below: OFF is the default AND the standard game, so a permanent «بدون سرس» pill would be
    // noise on every table and would cost the MEASURED single-line fit at 320px. What everyone
    // at the table needs to see is the NON-default — «here the Hakem may call نرس».
    ...(settings.hokmModes === true
      ? ([["modes", fa.hokm.modesShort]] as const satisfies readonly (readonly [
          IconName,
          string,
        ])[])
      : []),
    // ⚠️ AND کوت IS DELIBERATELY NOT HERE — do not add it back. It is the scoring rule FOR the
    // calls the line above already announces, so on this summary it read as a second, competing
    // fact about the same setting; and at 320–393px a sixth chip wrapped the block onto a second
    // row, which costs the felt ~29px and tightens every seat collision. The setting itself is
    // unchanged, and the INVITE CAPTION still spells it out («کوت روی این حکم‌ها هم حساب می‌شه»),
    // which is where a friend deciding whether to join actually needs it.
  ];
  return (
    <div data-testid="table-settings" className="flex flex-wrap items-center justify-center gap-1">
      {items.map(([icon, value]) => (
        <span
          key={icon}
          className="flex items-center gap-0.5 rounded-full border border-outline-variant bg-surface-container-low px-1 py-1 text-[11px] leading-none text-on-surface-variant"
        >
          <span className="text-on-surface-variant/80">
            <Icon name={icon} size={13} />
          </span>
          <span className="whitespace-nowrap font-bold text-on-surface">{value}</span>
        </span>
      ))}
    </div>
  );
}

/**
 * A PRIVATE TABLE'S INVITE PANEL — the caption, «کپی لینک میز» + Telegram's share sheet on ONE row,
 * and the friend list — rendered by the host's AND every guest's sheet: since card-47 (operator,
 * 2026-10-08) everyone already seated at a private table may invite from their friends list and
 * share the table's link. Nothing on the server was ever owner-only (`POST /invites` and
 * `POST /me/table/prepared` check no host; `roomId` rides every seat's lobby frame) — the only gate
 * was this screen's `isHost` branch. The host's sheet renders it inline; a guest's opens it from
 * ONE button, in the shared bottom sheet ({@link GuestInvite}). A FRAGMENT, so it lays out in its
 * parent's flex column exactly as the host's markup always did.
 */
function InvitePanel({
  gameId,
  roomId,
  seatedUserIds,
  copyState,
  onCopy,
  onShare,
  consumePendingInvite,
}: {
  gameId: string;
  roomId: string;
  /** card-98 item 1 — see {@link InviteFriendList}'s prop of the same name. */
  seatedUserIds: readonly string[];
  copyState: "idle" | "ok" | "failed";
  onCopy: () => void;
  onShare: () => void;
  /** The viewer HOSTS this table — see {@link InviteFriendList}'s prop of the same name. */
  consumePendingInvite: boolean;
}): ReactNode {
  return (
    <>
      <p className="m-0 text-center text-caption text-on-surface-variant">{t.inviteFriends}</p>
      {/* Invite actions on ONE row: «کپی لینک میز» (1/3) + Telegram's native multi-chat share
          sheet (2/3). Collapsing the link into a button reclaims the vertical space the
          always-visible URL field used to eat, so more friends fit in the list below. */}
      <div className="flex items-stretch gap-2">
        <button
          type="button"
          onClick={onCopy}
          aria-label={fa.online.copyLink}
          className="flex basis-1/3 items-center justify-center gap-1 rounded-lg border border-outline-variant bg-surface-container-low px-2 py-1.5 text-caption font-bold text-secondary transition-colors hover:text-gold-deep active:scale-95"
        >
          <Icon name={copyState === "ok" ? "check" : "copy"} size={15} />
          <span className="truncate">
            {copyState === "ok"
              ? t.copied
              : copyState === "failed"
                ? fa.online.copyFailed
                : t.copyLinkLabel}
          </span>
        </button>
        <button
          type="button"
          onClick={onShare}
          className="flex flex-[2] items-center justify-center gap-1.5 rounded-lg bg-gold-deep py-1.5 text-body-md font-bold text-ink-on-gold shadow-md transition-colors hover:bg-secondary active:scale-95"
        >
          <Icon name="share" size={17} />
          {t.sendInvite}
        </button>
      </div>
      {/* Direct friend invites to THIS table (paged + searchable, so a long friend list stays
          compact). Only the host's panel auto-sends a Friends-screen pick — and only at the
          table that pick created (`pendingInviteHere`). */}
      <InviteFriendList
        gameId={gameId}
        roomId={roomId}
        seatedUserIds={seatedUserIds}
        consumePendingInvite={consumePendingInvite}
      />
    </>
  );
}

/**
 * A GUEST'S WAY IN TO THE INVITE PANEL — ONE «دعوت دوستان» button in the waiting sheet that opens
 * the host's SAME {@link InvitePanel} (the caption, «کپی لینک میز» + the Telegram share, the friend
 * list) in the app's shared bottom {@link Sheet} (card-47 review F2).
 *
 * ⚠️ NOT THE PANEL INLINE. The waiting sheet is a flex SIBLING of the felt: whatever height it
 * gains comes off the disc, and the seats pinned to the disc's rim close in on the centre card.
 * MEASURED (`tools/waiting-room-probes/probe-sheet-layout.cjs`, 17 sizes + 3 best effort), the
 * panel drawn inline took a guest's room from 25 layout failures to 75 and covered the host's 👑 by
 * 10.8px on the operator's 384×823. On master a guest's whole sheet was 129px on a four-chip table
 * (every game but حکم with the calls; 173px with the calls' fifth chip); the rules row + the waiting
 * line + this button come to 109px (134px at 320, where five chips wrap), so the felt is never
 * smaller than master's — MEASURED after: 25 → 3 failures with five chips and 4 → 1 with four, none
 * new and none grown at any of the 20 sizes. Copy, share and the friend list stay one tap away. The
 * overlay lives inside the guest's sheet, so it unmounts with it: it never outlives the waiting room
 * (the start, a crown that moves to this player).
 */
function GuestInvite({
  gameId,
  roomId,
  seatedUserIds,
  copyState,
  onCopy,
  onShare,
}: {
  gameId: string;
  roomId: string;
  seatedUserIds: readonly string[];
  copyState: "idle" | "ok" | "failed";
  onCopy: () => void;
  onShare: () => void;
}): ReactNode {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-gold-deep py-1.5 text-body-md font-bold text-ink-on-gold shadow-md transition-colors hover:bg-secondary active:scale-95"
      >
        <Icon name="personAdd" size={17} />
        {fa.online.invite}
      </button>
      {open ? (
        <Sheet label={fa.online.invite} onClose={() => setOpen(false)}>
          {/* The Sheet's scroller is a block, and the panel is a Fragment — this column gives it
              the host's own `gap-2`. A guest never auto-sends a Friends-screen pick: this table
              is somebody else's. */}
          <div className="flex flex-col gap-2">
            <InvitePanel
              gameId={gameId}
              roomId={roomId}
              seatedUserIds={seatedUserIds}
              copyState={copyState}
              onCopy={onCopy}
              onShare={onShare}
              consumePendingInvite={false}
            />
          </div>
        </Sheet>
      ) : null}
    </>
  );
}

/**
 * ITEM 9 — the same arrangement stage, for منچ: the host assigns each player a COLOUR.
 *
 * On منچ a seat is not a partnership, it is a corner of the board and the colour of your four
 * tokens, so the ما/حریف panel says nothing useful. This shows one chip per seat carrying that
 * seat's actual arm colour, and the host swaps two players exactly as they would swap teams —
 * the very same `order` permutation, applied and broadcast server-side, so the arrangement every
 * player sees is one authoritative fact rather than four local guesses.
 *
 * Empty chairs take part and show as «ربات»: a bot will hold that colour at the deal.
 */
function ColorArrangePanel({
  players,
  seats,
  selfSeat,
  isHost,
  countdown,
  onSetTeams,
  onStart,
}: {
  players: readonly LobbyPlayer[];
  seats: number;
  selfSeat: number | null;
  isHost: boolean;
  /** ITEM 3 — seconds left on THE start countdown, which runs THROUGH this stage. */
  countdown: number;
  onSetTeams?: ((order: number[]) => void) | undefined;
  onStart?: (() => void) | undefined;
}): ReactNode {
  const [selected, setSelected] = useState<number | null>(null);
  const bySeat = new Map(players.map((p) => [p.seat, p]));

  const tap = (seatIdx: number): void => {
    if (!isHost) return;
    if (selected === null) {
      setSelected(seatIdx);
      return;
    }
    if (selected === seatIdx) {
      setSelected(null);
      return;
    }
    const order = Array.from({ length: seats }, (_x, i) => i);
    order[selected] = seatIdx;
    order[seatIdx] = selected;
    onSetTeams?.(order);
    setSelected(null);
  };

  return (
    <div className="flex flex-col gap-3" data-testid="color-arrange">
      <p className="m-0 text-center text-body-md font-bold text-on-surface">
        {t.colorsTitle}
        {/* ISSUE 3 — the countdown belongs to the OWNER, who is the one arranging. A guest is
            not being timed to pick anything (they cannot arrange at all — they just wait), so
            showing them a ticking number was pointless; they see «میزبان در حال تعیین رنگ‌هاست…»
            instead. The number is the host's start countdown, from 10. */}
        {isHost && countdown > 0 ? (
          <span data-testid="color-arrange-countdown" className="ms-2 text-secondary">
            {faDigits(countdown)}
          </span>
        ) : null}
      </p>
      <div className="grid grid-cols-2 gap-2">
        {Array.from({ length: seats }, (_x, seatIdx) => {
          const player = bySeat.get(seatIdx);
          const isYou = selfSeat !== null && seatIdx === selfSeat;
          const name = player ? (isYou ? fa.online.you : player.name) : t.teamsBotSeat;
          const on = selected === seatIdx;
          const tone = armColor(armOf(seatIdx, seats));
          return (
            <button
              key={seatIdx}
              type="button"
              aria-pressed={on}
              aria-label={`${fa.mensch.colorNames[armOf(seatIdx, seats)] ?? ""} — ${name}`}
              disabled={!isHost}
              onClick={() => tap(seatIdx)}
              className={`flex min-w-0 items-center gap-2 rounded-xl border px-2.5 py-2 text-right transition-all ${
                on
                  ? "border-secondary bg-secondary-container/40 shadow-[0_0_10px_rgb(var(--c-secondary)/0.35)]"
                  : "border-outline-variant bg-surface-container-low"
              } ${isHost ? "cursor-pointer active:scale-95" : "cursor-default"}`}
            >
              <span
                data-testid={`color-swatch-${seatIdx}`}
                className="h-6 w-6 flex-none rounded-full border border-black/25 shadow-inner"
                style={{ background: tone.base }}
              />
              <span className="min-w-0 flex-1">
                <span
                  className={`block truncate text-caption font-bold ${isYou ? "text-secondary" : "text-on-surface"}`}
                >
                  {name}
                </span>
              </span>
              {player?.isHost ? (
                <span role="img" aria-label={fa.online.host} className="flex-none text-[12px]">
                  👑
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
      {isHost ? (
        <>
          <p className="m-0 text-center text-caption text-on-surface-variant">{t.colorsHint}</p>
          {/* …PINNED like the partnership stage's (`teams-confirm-strip`): since card-a7 B6 this
              stage's sheet yields to the felt on a short screen too and scrolls, and the start must
              never need a scroll. Where nothing scrolls the layout is unchanged. */}
          <div
            data-testid="colors-confirm-strip"
            className="sticky bottom-0 z-10 -mx-md bg-surface-container-high px-md"
          >
            <button
              type="button"
              onClick={() => onStart?.()}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-gold-deep py-2.5 text-body-lg font-bold text-ink-on-gold shadow-md transition-colors hover:bg-secondary active:scale-95"
            >
              {t.teamsConfirm}
            </button>
          </div>
        </>
      ) : (
        <div className="flex items-center justify-center gap-2 text-body-md font-bold text-on-surface-variant">
          <Spinner />
          {t.colorsWaitingForHost}
        </div>
      )}
    </div>
  );
}

/**
 * The `teamSelect` stage panel (partnership games): the two partnerships derive from
 * SEAT PARITY — «تیم ۱» = seats 0&2, «تیم ۲» = seats 1&3 — and the HOST rearranges by
 * tapping two seats to swap them (`order[newSeat] = currentSeat`, validated
 * server-side). Everyone else sees the live arrangement read-only. The host's confirm
 * CTA sends the normal `start` and the server deals the seats exactly as arranged.
 *
 * EMPTY SEATS TAKE PART. The stage now runs as soon as a second human sits down, not
 * only on a full table, because «is my friend my partner (یار) or my opponent (حریف)?»
 * is exactly the question a host with one friend and two bots needs to answer — and
 * answering it means swapping that friend WITH an empty chair. Empty chairs show as
 * «ربات» (that is what will sit there at the deal).
 */
function TeamArrangePanel({
  players,
  seats,
  selfSeat,
  isHost,
  onSetTeams,
  onStart,
}: {
  players: readonly LobbyPlayer[];
  seats: number;
  selfSeat: number | null;
  isHost: boolean;
  onSetTeams?: ((order: number[]) => void) | undefined;
  onStart?: (() => void) | undefined;
}): ReactNode {
  const [selected, setSelected] = useState<number | null>(null);
  const bySeat = new Map(players.map((p) => [p.seat, p]));

  const tap = (seatIdx: number): void => {
    if (!isHost) return;
    if (selected === null) {
      setSelected(seatIdx);
      return;
    }
    if (selected === seatIdx) {
      setSelected(null);
      return;
    }
    // Swap the two tapped players: identity order with `selected`↔`seatIdx`.
    const order = Array.from({ length: seats }, (_x, i) => i);
    order[selected] = seatIdx;
    order[seatIdx] = selected;
    onSetTeams?.(order);
    setSelected(null);
  };

  const chip = (seatIdx: number): ReactNode => {
    const player = bySeat.get(seatIdx);
    const isYou = selfSeat !== null && seatIdx === selfSeat;
    // An empty chair at the team stage is not "waiting for someone" any more — the host
    // is about to deal, so a BOT is what sits there. Say so.
    const name = player ? (isYou ? fa.online.you : player.name) : t.teamsBotSeat;
    const on = selected === seatIdx;
    // the face the felt draws for this chair — the player's photo, else their own preset (A3)
    const face = lobbySeatFace(player, seatIdx);
    return (
      <button
        key={seatIdx}
        type="button"
        aria-pressed={on}
        aria-label={name}
        disabled={!isHost}
        onClick={() => tap(seatIdx)}
        className={`flex min-w-0 flex-1 items-center gap-2 rounded-xl border px-2 py-1.5 text-right transition-all ${
          on
            ? "border-secondary bg-secondary-container/40 shadow-[0_0_10px_rgb(var(--c-secondary)/0.35)]"
            : "border-outline-variant bg-surface-container-low"
        } ${isHost ? "cursor-pointer active:scale-95" : "cursor-default"}`}
      >
        <Avatar
          avatarId={face.avatarId}
          avatarPhotoId={face.avatarPhotoId}
          size={26}
          official={face.official}
        />
        <span className="min-w-0 flex-1">
          <span
            // `dir="auto"`: an RTL block clips the BEGINNING of a Latin name (`## Gotchas`).
            dir="auto"
            className={`block truncate text-caption font-bold ${isYou ? "text-secondary" : "text-on-surface"}`}
          >
            {name}
          </span>
        </span>
        {player?.isHost ? (
          <span role="img" aria-label={fa.online.host} className="flex-none text-[12px]">
            👑
          </span>
        ) : null}
      </button>
    );
  };

  // Seat parity IS the partnership: even seats vs odd seats.
  const team1 = Array.from({ length: seats }, (_x, i) => i).filter((i) => i % 2 === 0);
  const team2 = Array.from({ length: seats }, (_x, i) => i).filter((i) => i % 2 === 1);

  return (
    /* NO heading here: the centre card over the felt already says «چیدن تیم‌ها», and this sheet
       sits directly beneath it — the two were on screen together. Everything in this panel is one
       step tighter for the same reason the card shrank: MEASURED, the panel went 276 → 228px and
       the sheet 292–308 → 244–260px, which is what clears the seats at 360×640 and 440×640 (the
       card alone does not). It also puts «تأیید و شروع بازی» back INSIDE the scroller at 320×568,
       where the host previously had to scroll the sheet to reach their own confirm button. */
    <div className="flex flex-col gap-2" data-testid="team-arrange">
      <div className="flex gap-2">
        {(
          [
            [t.team1, team1],
            [t.team2, team2],
          ] as const
        ).map(([label, teamSeats]) => (
          <div
            key={label}
            className="flex min-w-0 flex-1 flex-col gap-1.5 rounded-xl border border-gold-deep/20 bg-surface-container p-2"
          >
            <span className="text-center text-caption font-bold text-secondary">{label}</span>
            {teamSeats.map((s) => chip(s))}
          </div>
        ))}
      </div>
      {isHost ? (
        <>
          <p className="m-0 text-center text-caption text-on-surface-variant">{t.teamsHint}</p>
          {/* THE CONFIRM NEVER NEEDS A SCROLL (card-a7, the operator plays on Telegram Desktop zoomed
              in): on a short screen this stage's sheet yields to the felt (`TEAMS_SHEET_MAX_H`) and
              scrolls, so the button is `sticky` at the scroller's bottom edge and the teams scroll
              BEHIND it. The strip is exactly the button's height and spans the scroller's padding in
              the sheet's own colour, so where nothing scrolls the layout is unchanged.
              `tools/waiting-room-probes/probe-teams-judge.cjs` asserts it at every walk look. */}
          <div
            data-testid="teams-confirm-strip"
            className="sticky bottom-0 z-10 -mx-md bg-surface-container-high px-md"
          >
            <button
              type="button"
              // Wrapped, never `onClick={onStart}`: React would hand the MouseEvent to
              // the handler (a DOM event is not serializable). The confirm deals the
              // seats exactly as arranged above — any empty chair becomes a bot.
              onClick={() => onStart?.()}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-gold-deep py-2 text-body-md font-bold text-ink-on-gold shadow-md transition-colors hover:bg-secondary active:scale-95"
            >
              {t.teamsConfirm}
            </button>
          </div>
        </>
      ) : (
        <div className="flex items-center justify-center gap-2 text-body-md font-bold text-on-surface-variant">
          <Spinner />
          {t.teamsWaitingForHost}
        </div>
      )}
    </div>
  );
}

/**
 * THE ARRANGEMENT STAGE'S SHEET YIELDS TO THE FELT ON A SHORT SCREEN (card-a7, the پاسور walk's
 * «چیدن تیم‌ها» at 300×667 under text ×1.3). The sheet is a flex sibling of the felt, capped at 46 %
 * of the app height — and there the team panel used all of it, <main> was left 151 px, and the felt
 * fell to its 120 px floor: the four rim seats (80 px boxes at 40 % of the radius) landed on one
 * another and on the centre card (9 overlaps, measured with the walk's own judge on the dev harness).
 * So in this stage the sheet also leaves room for a felt of at least {@link TEAMS_FELT_MIN} px — the
 * header (inset + 64), <main>'s padding (24), the seats' overhang reserve (72), the sheet's own
 * border and bottom padding (1 + 16 + inset) — and scrolls inside what is left, its confirm pinned
 * (`teams-confirm-strip`) so it never needs a scroll. منچ's colour stage is not this layout and keeps
 * the plain cap. On a screen that already has the room the 46 % cap still decides. MEASURED with the walk's judge at its nine looks
 * (`tools/waiting-room-probes/probe-teams-judge.cjs`): 4 failing → 0; the felt is unchanged at the
 * operator's 384×823 ×1–×1.5 and at 390×844, and grows to 241 px at 384×590, 384×569@1.5, 300×667
 * ×1.3 and 320×492@1.5 (from 202 / 181 / 120 / 120), where the sheet scrolls instead.
 */
const TEAMS_FELT_MIN = 240;
/** …and never so low that the pinned confirm (40 px) plus the scroller's top padding no longer fit:
 *  on the smallest Desktop panels (307×472@1.25, 256×393@1.5) the felt's need alone exceeds the app
 *  height, and the confirm would have hung below a collapsed sheet. */
const TEAMS_SHEET_MIN_H = 64;
/** The sheet's cap that leaves the felt `feltNeed` (a CSS length): the header (inset + 64), <main>'s
 *  padding (24), the seats' overhang reserve (72), the sheet's own border and bottom padding (1 + 16
 *  + inset) — floored at {@link TEAMS_SHEET_MIN_H}. */
const sheetMaxH = (feltNeed: string): string =>
  `max(${TEAMS_SHEET_MIN_H}px, min(calc(var(--app-h) * 0.46), calc(var(--app-h) - max(env(safe-area-inset-top), var(--tg-safe-top, 0px)) - max(env(safe-area-inset-bottom), var(--tg-safe-bottom, 0px)) - ${feltNeed})))`;
const TEAMS_SHEET_MAX_H = sheetMaxH(`${64 + 24 + 72 + 1 + 16 + TEAMS_FELT_MIN}px`);
/** …and the WAITING stages' (a private table's host and guest, the public lone player — the B6
 *  review, I1): the centre card is never drawn below its floor, so the felt they leave it is larger,
 *  up to the felt's own width cap — {@link WAITING_FELT_NEED}: at the operator's 384×823 under text
 *  ×1.5 the 240 px felt left a full host table's card no room above ×0.58. */
const WAITING_FELT_NEED = "min(82vw, 280px)";
const WAITING_SHEET_MAX_H = sheetMaxH(`${64 + 24 + 72 + 1 + 16}px - ${WAITING_FELT_NEED}`);

/**
 * THE CENTRE CARD NEVER COVERS A SEAT (card-a7 B6, operator 2026-10-21: «nothing left as a known
 * limitation»). The card is centred in the felt and the seats are pinned to its rim (at 40 % of its
 * side), so how much of the middle is free depends on the felt's size, the game's seats, the names in
 * them and the text zoom — and the card's own size on its stage and the zoom. MEASURED on the dev
 * harness (`tools/waiting-room-probes/probe-seats-covered.cjs`), every game, before this: the card
 * sat on a side seat's avatar from a felt of ~290 px down — the host's card on the operator's own
 * 384×823 at ×1, every zoomed look, every Telegram Desktop panel.
 *
 * So the card is SCALED to the largest size at which everything it paints (its box and every child,
 * an overflowing line included) clears every painted part of every seat (`data-seat-part`: the disc,
 * its badges, the name, «ربات») by {@link CARD_SEAT_GAP} on at least one axis — a transform, so
 * nothing else moves — and drawn at its own size wherever it already fits. NEVER BELOW A FLOOR (the
 * B6 review, I1: a half-size card reads as a defect): {@link CARD_SCALE_FLOOR}, and no text under the
 * app's smallest ({@link APP_MIN_TEXT_PX} at the screen's text zoom). Where the card cannot clear
 * the seats above that floor it sheds its caption row (`tight`: the game's name and the count — the
 * ring says who is seated), and where even that cannot, it steps aside (`none`) — EXCEPT the private
 * table's WAITING card (`whole`, card-98 item 7, operator 2026-10-28: «put the old centre card back
 * as it was before 10-22, at its old size wherever it fits. Only where it doesn't fit — small screens
 * or text zoom — shrink it to fit, so the button always shows inside the card and covers no seat»):
 * that card is always whole — no floor, no shedding, never aside — at the scale that clears every
 * seat, 1 wherever it fits. Re-read before the first paint and whenever
 * the felt, the card or a seat resizes or a web font lands; the form starts over per phase, roster
 * and felt size. Unlaid-out (happy-dom), the felt has no size: 1, the whole card.
 */
const CARD_SEAT_GAP = 4;
/** The card is never drawn smaller than this… */
const CARD_SCALE_FLOOR = 0.75;
/** …and the WHOLE waiting card (card-98 item 7) never below this last resort: «the button always
 *  shows inside the card» outranks a seat's last pixels on a screen no named look comes near (the
 *  census's lowest is ×0.562, the operator's ×1.5; the review's F-M1). */
const WHOLE_CARD_MIN_SCALE = 0.5;
/** …nor any of its text below the app's smallest UI text (`text-[10px]`, «ربات» under a seat), at
 *  the screen's text zoom. */
const APP_MIN_TEXT_PX = 10;
/** The card's forms, richest first: whole; without its caption row; stepped aside. */
type CardForm = "full" | "tight" | "none";
interface CardFit {
  readonly scale: number;
  readonly form: CardForm;
  /** The width the side seats leave the card (layout px) — its lines WRAP to it before it scales;
   *  null where no seat sits beside it (a table for two). */
  readonly band: number | null;
}
const WHOLE_CARD: CardFit = { scale: 1, form: "full", band: null };
function useCentreCardFit(
  feltRef: { current: HTMLElement | null },
  cardRef: { current: HTMLElement | null },
  key: string,
  /** The private table's waiting card (card-98 item 7): always WHOLE — never below a floor, never
   *  shedding its caption row, never stepped aside, no band to wrap to (its lines are `nowrap`, as
   *  before 10-22) — drawn at the scale that clears the seats: 1 wherever it fits. */
  whole = false,
): CardFit {
  const [fit, setFit] = useState<CardFit>(WHOLE_CARD);
  // a new phase or roster: the richest form first
  const [seenKey, setSeenKey] = useState(key);
  if (seenKey !== key) {
    setSeenKey(key);
    setFit((prev) => ({ ...WHOLE_CARD, band: prev.band }));
  }
  const form = fit.form;
  const feltW = useRef(0);
  useLayoutEffect(() => {
    const felt = feltRef.current;
    const card = cardRef.current;
    if (!felt || !card) return undefined;
    const read = (): void => {
      const box = felt.getBoundingClientRect();
      if (!(box.width > 0 && felt.offsetWidth > 0 && card.offsetWidth > 0)) {
        setFit((prev) => (prev === WHOLE_CARD ? prev : WHOLE_CARD));
        return;
      }
      // a new felt size (a viewport, a sheet, a zoom): the richest form again
      if (Math.abs(box.width - feltW.current) > 0.5) {
        const first = feltW.current === 0;
        feltW.current = box.width;
        if (!first && form !== "full") {
          setFit((prev) => ({ ...WHOLE_CARD, band: prev.band }));
          return;
        }
      }
      const cx = box.left + box.width / 2;
      const cy = box.top + box.height / 2;
      // the screen's px per layout px (<main>'s reveal scales the felt from 0.8), and the card's
      // own current scale: what it paints, read through both, back in its own layout px
      const px = box.width / felt.offsetWidth;
      // THE BAND: the width the seats BESIDE the card leave it — every part wholly left or right of
      // the felt's centre within the height the seats ABOVE and BELOW it leave (a side seat's
      // name runs under its disc: at 256×393 a long one is the nearest thing to the card)
      // (by SEAT: a seat across the felt's centre column — above or below the card — bounds the
      // height, every part of it, its badge included; a seat wholly beside it bounds the width)
      let vRoom = Infinity;
      const beside: DOMRect[] = [];
      for (const seatEl of felt.querySelectorAll<HTMLElement>("[data-seat]")) {
        const sb = seatEl.getBoundingClientRect();
        const across = sb.left <= cx && sb.right >= cx;
        for (const part of seatEl.querySelectorAll<HTMLElement>("[data-seat-part]")) {
          const q = part.getBoundingClientRect();
          if (!(q.width > 0 && q.height > 0)) continue;
          if (!across) beside.push(q);
          else
            vRoom = Math.min(
              vRoom,
              (q.top > cy ? q.top - cy : q.bottom < cy ? cy - q.bottom : 0) / px,
            );
        }
      }
      let band = Infinity;
      for (const q of beside) {
        const top = (q.top - cy) / px;
        const bottom = (q.bottom - cy) / px;
        if (bottom < -vRoom || top > vRoom) continue;
        const inner = q.left > cx ? (q.left - cx) / px : q.right < cx ? (cx - q.right) / px : 0;
        band = Math.min(band, 2 * (inner - CARD_SEAT_GAP));
      }
      // (a whole card does not wrap to the band: its lines are `nowrap`, it only scales)
      const bandPx = !whole && Number.isFinite(band) ? Math.max(0, Math.floor(band)) : null;
      if (bandPx !== fit.band) {
        // the card's lines wrap to the band first: lay it out at that width, then measure
        setFit((prev) => ({ ...prev, band: bandPx }));
        return;
      }
      if (form === "none") return;
      const now = card.getBoundingClientRect().width / (card.offsetWidth * px) || 1;
      const k0 = px * now;
      let l = 0;
      let r = 0;
      let t = 0;
      let b = 0;
      let minFont = Infinity;
      for (const el of [card, ...card.querySelectorAll<HTMLElement>("*")]) {
        const q = el.getBoundingClientRect();
        if (!(q.width > 0 && q.height > 0)) continue;
        l = Math.min(l, (q.left - cx) / k0);
        r = Math.max(r, (q.right - cx) / k0);
        t = Math.min(t, (q.top - cy) / k0);
        b = Math.max(b, (q.bottom - cy) / k0);
        if (el.childElementCount === 0 && (el.textContent ?? "").trim() !== "")
          minFont = Math.min(minFont, parseFloat(getComputedStyle(el).fontSize) || Infinity);
      }
      let k = 1;
      for (const part of felt.querySelectorAll<HTMLElement>("[data-seat-part]")) {
        const q = part.getBoundingClientRect();
        if (!(q.width > 0 && q.height > 0)) continue;
        // the part around the felt's centre in layout px, grown by the gap; the card's paint
        // (l…r, t…b around the same centre) scaled by k clears it when ONE axis separates them
        const pl = (q.left - cx) / px - CARD_SEAT_GAP;
        const pr = (q.right - cx) / px + CARD_SEAT_GAP;
        const pt = (q.top - cy) / px - CARD_SEAT_GAP;
        const pb = (q.bottom - cy) / px + CARD_SEAT_GAP;
        const clear = Math.max(
          pl > 0 && r > 0 ? pl / r : 0,
          pr < 0 && l < 0 ? pr / l : 0,
          pt > 0 && b > 0 ? pt / b : 0,
          pb < 0 && t < 0 ? pb / t : 0,
        );
        k = Math.min(k, clear);
      }
      k = Math.max(0, Math.floor(k * 1000) / 1000);
      // the floor: the scale's own, and the smallest text's at the screen's text zoom
      const zoom = parseFloat(getComputedStyle(document.body).fontSize) / 16 || 1;
      const floor = Math.max(
        CARD_SCALE_FLOOR,
        Number.isFinite(minFont) ? (APP_MIN_TEXT_PX * zoom) / minFont : 0,
      );
      // …except the whole (waiting) card: never shed, never aside — the scale that clears the
      // seats, 1 wherever it fits (card-98 item 7, the operator's ruling), and never below
      // WHOLE_CARD_MIN_SCALE, where the start must still show even if the card then touches a seat
      if (whole || k + 1e-6 >= floor) {
        const s = whole ? Math.max(WHOLE_CARD_MIN_SCALE, k) : k;
        setFit((prev) =>
          prev.scale === s && prev.form === form ? prev : { scale: s, form, band: prev.band },
        );
        return;
      }
      setFit((prev) => ({ scale: 1, form: form === "full" ? "tight" : "none", band: prev.band }));
    };
    read();
    // a web font landing re-measures every line: the richest form again
    const fontsIn = (): void => {
      if (form === "full") read();
      else setFit((prev) => ({ ...WHOLE_CARD, band: prev.band }));
    };
    const fonts: EventTarget | undefined = document.fonts;
    fonts?.addEventListener("loadingdone", fontsIn);
    const ro = typeof window.ResizeObserver === "function" ? new window.ResizeObserver(read) : null;
    if (ro) {
      ro.observe(felt);
      ro.observe(card);
      for (const seatEl of felt.querySelectorAll<HTMLElement>("[data-seat]")) ro.observe(seatEl);
    }
    return () => {
      ro?.disconnect();
      fonts?.removeEventListener("loadingdone", fontsIn);
    };
  }, [feltRef, cardRef, key, form, fit.band, whole]);
  return fit;
}

/** An overlay's fit (`useOverlayClear`): moved by `dy` px, then scaled by `k` about `origin`. */
interface OverlayFit {
  readonly dy: number;
  readonly k: number;
  readonly origin: string;
}
const NO_FIT: OverlayFit = { dy: 0, k: 1, origin: "50% 50%" };
/**
 * …AND NEITHER DOES AN OVERLAY OF <main> (card-a7 B6): the full-house banner floats at <main>'s top
 * and the public table's invite line at its bottom (`pb-10`), in the band the seat ring leaves — and
 * on Telegram Desktop's shortest panels (307×472@1.25, 256×393@1.5) that band is shorter than they
 * are: the banner lay on the top seat, the invite line on «شما». So the overlay (`box`, the painted
 * pill; its absolute wrapper is moved) first gives up its own padding towards <main>'s edge — the
 * banner its `pt-3`, the line its `pb-10`, all but 2 px — and only then scales, about its edge, to
 * clear every seat part it spans by {@link CARD_SEAT_GAP}. Layout px throughout (the reveal's scale
 * divided out); it never moves anything it reads. Unlaid-out (happy-dom): no fit.
 */
function useOverlayClear(
  feltRef: { current: HTMLElement | null },
  boxRef: { current: HTMLElement | null },
  edge: "top" | "bottom",
  key: string,
): OverlayFit {
  const [fit, setFit] = useState<OverlayFit>(NO_FIT);
  useLayoutEffect(() => {
    const felt = feltRef.current;
    const box = boxRef.current;
    const wrap = box?.parentElement;
    const main = felt?.offsetParent;
    if (!felt || !box || !wrap || !(main instanceof HTMLElement)) {
      setFit(NO_FIT);
      return undefined;
    }
    const read = (): void => {
      const fb = felt.getBoundingClientRect();
      if (!(fb.width > 0 && felt.offsetWidth > 0 && box.offsetHeight > 0)) {
        setFit(NO_FIT);
        return;
      }
      const px = fb.width / felt.offsetWidth;
      const cx = fb.left + fb.width / 2;
      const cy = fb.top + fb.height / 2;
      // the overlay's own box in <main>'s layout px (its wrapper is <main>'s absolute child)
      const top = wrap.offsetTop + box.offsetTop;
      const h = box.offsetHeight;
      const left = wrap.offsetLeft + box.offsetLeft;
      const right = left + box.offsetWidth;
      const midX = felt.offsetLeft + felt.offsetWidth / 2;
      const midY = felt.offsetTop + felt.offsetHeight / 2;
      let reach = edge === "top" ? Infinity : -Infinity;
      for (const part of felt.querySelectorAll<HTMLElement>("[data-seat-part]")) {
        const r = part.getBoundingClientRect();
        if (!(r.width > 0 && r.height > 0)) continue;
        const l = midX + (r.left - cx) / px;
        const rr = midX + (r.right - cx) / px;
        if (rr <= left || l >= right) continue;
        reach =
          edge === "top"
            ? Math.min(reach, midY + (r.top - cy) / px - CARD_SEAT_GAP)
            : Math.max(reach, midY + (r.bottom - cy) / px + CARD_SEAT_GAP);
      }
      const over = edge === "top" ? top + h - reach : reach - top;
      if (!Number.isFinite(over) || over <= 0) {
        setFit(NO_FIT);
        return;
      }
      // the padding towards <main>'s edge first (2 px kept), then the scale about that edge
      const slack = Math.max(0, edge === "top" ? top - 2 : main.clientHeight - (top + h) - 2);
      const dy = Math.min(over, slack);
      const rest = over - dy;
      const k = rest > 0 ? Math.max(0, Math.floor(((h - rest) / h) * 1000) / 1000) : 1;
      const origin = `50% ${edge === "top" ? box.offsetTop : box.offsetTop + h}px`;
      const next = { dy: edge === "top" ? -dy : dy, k, origin };
      setFit((prev) =>
        prev.dy === next.dy && prev.k === next.k && prev.origin === next.origin ? prev : next,
      );
    };
    read();
    const fonts: EventTarget | undefined = document.fonts;
    fonts?.addEventListener("loadingdone", read);
    const ro = typeof window.ResizeObserver === "function" ? new window.ResizeObserver(read) : null;
    if (ro) for (const el of [felt, box, main]) ro.observe(el);
    return () => {
      ro?.disconnect();
      fonts?.removeEventListener("loadingdone", read);
    };
  }, [feltRef, boxRef, edge, key]);
  return fit;
}
/** The fit as a wrapper's style (none when it already clears). */
const fitStyle = (fit: OverlayFit): { transform?: string; transformOrigin?: string } =>
  fit.dy === 0 && fit.k === 1
    ? {}
    : { transform: `translateY(${fit.dy}px) scale(${fit.k})`, transformOrigin: fit.origin };

export function WaitingRoom({
  lobby,
  seat,
  onLeave,
  title = fa.online.title,
  isHost = false,
  onStart,
  onSetTeams,
  gameId = "",
  onInviteSeat,
  pendingInvite = null,
  onCancelInvite,
  onPlaySolo,
}: {
  lobby: LobbyState | null;
  seat: number | null;
  onLeave: () => void;
  /** Game-scoped online heading (e.g. «بازی آنلاین حکم»), shown as the center
   * card's small caption above the waiting title. */
  title?: string;
  /** Whether the viewer hosts this (private) table — gates the Start control. NOT the invite
   *  panel: every seated player of a private table invites (card-47) — the host from the panel
   *  inline, a guest from the «دعوت دوستان» button's sheet; only the host's panel auto-sends a
   *  Friends-screen pick, and only at the table that pick created (card-a5 item 15). */
  isHost?: boolean;
  /** Host-only: start the private table. Carries the {@link StartIntent}: the
   *  bot-fill start sends `{ withBots: true }`, the full-table start sends
   *  nothing (a callback that ignores the argument still works). */
  onStart?: (intent?: StartIntent) => void;
  /** Host-only, `teamSelect` phase: apply a seat arrangement (who partners with
   * whom) — `order[newSeat]` = the current seat of the player moving there. */
  onSetTeams?: (order: number[]) => void;
  /** The game id, for the invite deep-link (private tables only). */
  gameId?: string;
  /** PUBLIC tables only: a seated player tapped the «+» on an empty chair — open the friend
   *  picker for it. Omitted (private tables, or no window left to invite into) → no «+». */
  onInviteSeat?: (seat: number) => void;
  /** PUBLIC tables: my one live invite to this table, if any — replaces the badges with a
   *  single pending line (there is no bottom sheet on a public table to host a panel). */
  pendingInvite?: { id: string; name: string } | null;
  /**
   * Leave this public table and start a SOLO game of the same game instead.
   *
   * A public table's fill countdown waits for a second human, so a lone player can be sitting
   * there with nothing counting and no way to make anything happen — a public table is ownerless,
   * so there is no «با ربات‌ها شروع کن» either. Bots are what solo mode is FOR, and saying so is
   * the difference between «held» and «hung». Omitted (private tables) ⇒ no button.
   */
  onPlaySolo?: () => void;
  /** Withdraw {@link pendingInvite}. */
  onCancelInvite?: (inviteId: string) => void;
}): ReactNode {
  const api = useSession((s) => s.api);
  const seats = lobby?.seats ?? 4;
  const players = lobby?.players ?? [];
  // card-98 item 1 — who is at this table now (app ids of seated humans): «پیوست» only for them.
  const seatedUserIds = players.flatMap((p) => (!p.isBot && p.user ? [p.user.userId] : []));
  const bySeat = new Map(players.map((p) => [p.seat, p]));
  // Sound an arrival / a departure — for the people ALREADY at the table, never for the
  // one who just walked in or out (see `useRosterSfx`). Keyed on WHO is here (identity),
  // so the host swapping two chairs at the team stage is not mistaken for someone leaving.
  useRosterSfx(
    seat === null
      ? null
      : players
          .filter((p) => p.seat !== seat)
          .map((p) => p.user?.userId ?? p.name)
          .sort()
          .join("|"),
  );
  const starting = lobby?.phase === "starting";
  const teamSelect = lobby?.phase === "teamSelect";
  // ITEM 9 — منچ arranges COLOURS at this stage, not partnerships.
  const arrangesColors = gameId === "mensch";
  const joined = players.length;
  // The "full house" celebration: EVERY seat is filled by a HUMAN and the start
  // countdown is running. Bots only enter the roster at real game start (the
  // server flips the phase to "playing" before it fills empty seats), so during
  // "starting" the roster is always human — the `!isBot` guard makes that intent
  // explicit AND keeps the bot-fill path (a partial table that will start with
  // bots) off the banner: it must keep today's plain countdown rendering.
  const fullHouse = starting && joined === seats && players.every((p) => !p.isBot);
  const isPrivate = lobby?.isPrivate ?? false;
  // The public quick-match pool vs. a host-created friend table — the design's
  // `general` switch that drives the whole variant.
  const general = !isPrivate;
  const settings = lobby?.settings;
  const roomId = lobby?.roomId ?? "";
  const countdown = lobby?.countdown ?? 0;
  /** Is a clock actually ticking? (`starting` is not enough — منچ's colour stage runs one too.) */
  const fillRunning = countdown > 0;
  /** The fill countdown FROZEN because this table dropped back to one human (0 = never started). */
  const countdownHeld = lobby?.countdownPaused ?? 0;
  /**
   * A PUBLIC table with NOTHING counting: either nobody has joined you yet, or the second human
   * left and the clock is being held for the next one. Never true on a private table (its host
   * owns the start) nor while a stage is arranging.
   */
  const nothingCounting = general && !starting && !teamSelect;
  const link = inviteLink(gameId, roomId);
  // the centre card's fit inside the seat ring (`useCentreCardFit`): re-read per phase and roster
  const feltRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const fitKey = `${seats}|${lobby?.phase ?? ""}|${seat ?? ""}|${players
    .map((p) => `${p.seat}:${p.name}:${p.isBot ? 1 : 0}:${p.isHost ? 1 : 0}`)
    .join(",")}`;
  /** The private table's WAITING card — the host's (with the start) and a guest's: the card as it was
   *  before 10-22, always whole, scaled only where it must be (card-98 item 7, `useCentreCardFit`). */
  const wholeCard = !starting && !teamSelect && !general;
  const {
    scale: cardScale,
    form: cardForm,
    band: cardBand,
  } = useCentreCardFit(feltRef, cardRef, fitKey, wholeCard);
  /** The card's caption row (the game's name, the count) — shed where the card cannot clear the
   *  seats at its floor (`useCentreCardFit`'s `tight`). */
  const cardCaption = cardForm === "full";
  // …and <main>'s two overlays clear the seats too (`useOverlayClear`)
  const bannerRef = useRef<HTMLDivElement>(null);
  const hintRef = useRef<HTMLSpanElement>(null);

  // ── PUBLIC-TABLE FRIEND INVITE (the per-seat «+») ────────────────────────────────────
  // A public table is OWNERLESS, so unlike the host panel this is open to any seated player.
  // Withheld once the fill window is nearly gone: the invite's TTL is clamped to the remaining
  // countdown, so under ~2s the friend would tap into a table that had already dealt.
  // ONE live invite at a time — that IS the "one friend" rule, and it also gives the pending
  // state somewhere to live (a public table renders no bottom sheet — see the `starting` gate
  // on the sheet below — so there is no panel to put it in).
  // ⚠️ `!fillRunning || countdown > 2` — the end-of-window guard applies only while a window is
  // actually running. With the countdown waiting for a second human there is no deadline to run
  // out of, and inviting a friend is the single most useful thing a lone player can do: gating on
  // a bare `countdown > 2` would have hidden the «+» at exactly the moment it matters most.
  const canInviteSeat =
    general &&
    onInviteSeat !== undefined &&
    seat !== null &&
    !pendingInvite &&
    (!fillRunning || countdown > 2);
  const inviteFor = (seatIndex: number): (() => void) | undefined =>
    canInviteSeat && !bySeat.has(seatIndex) && seatIndex !== seat
      ? () => onInviteSeat?.(seatIndex)
      : undefined;
  const showInviteHint = canInviteSeat && players.length < seats;
  /**
   * ⚠️ PUBLIC ONLY — and that gate is the whole point.
   *
   * The container passes `pendingInvite` unconditionally (it is simply «my one live invite to this
   * room»), but on a PRIVATE table that state ALREADY has a home: the «لغو دعوت» pill on the
   * friend's own row in the bottom sheet's invite list. Rendering the overlay there too said the
   * same thing twice AND — being an `absolute … bottom-0` band over the felt — landed on top of the
   * HOST'S OWN seat name (hit-tested at 360×640, 360×740, 440×640 and 480×730 with
   * `elementFromPoint`, the pill owning the pixel under the label every time).
   *
   * A PUBLIC table renders no bottom sheet at all, which is the only reason this overlay exists —
   * see the prop's own doc comment, which has said «PUBLIC tables» since it was written.
   */
  const overlayInvite = general ? (pendingInvite ?? null) : null;
  const bannerFit = useOverlayClear(feltRef, bannerRef, "top", `${fitKey}|${fullHouse}`);
  const hintFit = useOverlayClear(
    feltRef,
    hintRef,
    "bottom",
    `${fitKey}|${showInviteHint}|${overlayInvite?.id ?? ""}`,
  );

  // Invite affordances: copy writes to the clipboard; share opens TELEGRAM'S
  // OWN share sheet (the user picks the chat(s) there — Telegram's sheet
  // supports selecting several at once). Each recipient's tap follows the
  // `?start=t_<game>_<roomId>` deep-link straight into THIS table, with the
  // existing expiry/room-full/already-started handling on the join. Outside
  // Telegram (dev/browser) it degrades to the web share sheet, then copy.
  /**
   * ⚠️ ONLY CLAIM «کپی شد» ONCE THE WRITE HAS ACTUALLY RESOLVED, AND SAY SO WHEN IT HAS NOT.
   *
   * This was `void navigator.clipboard?.writeText(link); setCopied(true);` — two defects in two
   * lines. `void` marks a promise intentionally unawaited; it does NOT swallow a rejection, so a
   * refused write escaped as an unhandled rejection and was reported as a `crash-router` ERROR
   * (seen in production: «NotAllowedError: … 'writeText' … Document is not focused», the Telegram
   * WebView case where focus sits elsewhere when the tap lands). And `setCopied(true)` fired
   * regardless, so the host read «کپی شد ✓», pasted, and got whatever was on the clipboard before —
   * the UI asserting a success the layer beneath never delivered. `ShareInvite` already had this
   * right; this mirrors it.
   */
  const [copyState, setCopyState] = useState<"idle" | "ok" | "failed">("idle");
  const copy = (): void => {
    const flash = (next: "ok" | "failed"): void => {
      setCopyState(next);
      setTimeout(() => setCopyState("idle"), 1500);
    };
    const write = navigator.clipboard?.writeText(link);
    // No Clipboard API at all (an old WebView) is a failure the player must SEE, not a silent no-op.
    if (!write) return flash("failed");
    write.then(() => flash("ok")).catch(() => flash("failed"));
  };
  const share = (): void => {
    // The link sheet: a URL + a line of text. It is what shipped before, and it
    // stays as the FALLBACK — the prepared card below needs Bot API 8.0 on the
    // client, and a player on an older Telegram must still be able to invite.
    const shareLink = (): void => {
      const gameName = GAMES.find((g) => g.id === gameId)?.fa ?? fa.appName;
      try {
        if (shareURL.isAvailable()) {
          shareURL(link, t.shareText(gameName));
          return;
        }
      } catch {
        // SDK not initialized (non-Telegram env) → web fallbacks below.
      }
      const nav = navigator as { share?: (d: { url: string; title?: string }) => Promise<unknown> };
      if (nav.share) void nav.share({ url: link, title: fa.appName }).catch(() => undefined);
      else copy();
    };
    // Preferred: Telegram's native multi-chat sheet carrying THIS GAME'S own
    // invitation text, with no link preview (a prepared inline message minted by
    // the API). Any refusal — old client, unconfigured deployment — degrades to
    // the link sheet instead of leaving the player with a dead button.
    if (!isGameId(gameId) || roomId === "") {
      shareLink();
      return;
    }
    void shareTableInvite(api, gameId, roomId).then((status) => {
      // "declined" = the player closed Telegram's picker themselves; the sheet did
      // its job, so we must not answer a cancel with a second sheet.
      if (status !== "shared" && status !== "declined") shareLink();
    });
  };

  // WHAT THE HOST MAY START. Bot-fill is ALWAYS available now (the retired «تکمیل با
  // ربات» toggle is gone): a not-full table starts «با ربات‌ها شروع کن», a full HUMAN
  // table starts plainly. Public quick-match tables are ownerless, so no start control
  // is drawn for them at all.
  const full = joined >= seats;
  // A bot-fill start still declares itself on the wire; on a partnership table it now
  // opens the team stage first (with the empty chairs shown as the bots they will be),
  // because who your one human friend partners with is a real choice.
  const botStart = !full;
  const startLabel = botStart ? t.startWithBot : fa.online.start;

  return (
    <div
      dir="rtl"
      className="flex h-[var(--app-h)] flex-col overflow-hidden text-on-surface"
      style={{
        background:
          "radial-gradient(ellipse at center, rgb(var(--c-surface-container)) 0%, rgb(var(--c-surface-dim)) 100%)",
      }}
    >
      {/* Header — brand wordmark, a leave (exit) control, and a mode glyph. */}
      <header
        className="flex-none border-b border-outline-variant bg-surface-container-low"
        style={{ paddingTop: "max(env(safe-area-inset-top), var(--tg-safe-top, 0px))" }}
      >
        {/* `max-height: 440px` (Telegram Desktop's 256×393 @1.5 — the only named size that short):
            the header gives 16 px and <main> its padding to the felt, which was 153 px there and put
            the host's 👑 on a neighbour's name (card-a7; `probe-teams-judge.cjs`). */}
        <div className="mx-auto flex h-16 max-w-[640px] items-center justify-between px-gutter [@media(max-height:440px)]:h-12">
          <button
            type="button"
            aria-label={fa.online.leave}
            onClick={onLeave}
            className="flex h-9 w-9 flex-none items-center justify-center rounded-full text-gold-deep transition-transform hover:bg-surface-bright/30 active:scale-95"
          >
            <GameIcon type="exit" size={22} />
          </button>
          <div className="flex min-w-0 items-center gap-2">
            <img
              src={logoUrl}
              alt=""
              className="h-8 w-8 flex-none rounded-full border border-gold-deep/50 object-cover"
            />
            <h1 className="truncate text-headline-md font-extrabold tracking-tight text-gold-deep">
              {fa.appName}
            </h1>
          </div>
          <div className="flex h-9 flex-none items-center justify-end">
            {general ? (
              <span className="rounded-full border border-outline-variant bg-surface-container px-2 py-1 text-[10px] font-bold text-on-surface-variant">
                {t.noHost}
              </span>
            ) : isHost && lobby?.idleDeadline ? (
              // The HOST sees a live countdown to auto-close (instead of the decorative watch) — the
              // table closes if they don't finalize «یار و حریف» / start it in time.
              <IdleCountdown deadline={lobby.idleDeadline} />
            ) : (
              <span className="text-gold-deep/70">
                <Icon name="timer" size={22} />
              </span>
            )}
          </div>
        </div>
      </header>

      {/* The felt table with seats around the rim + the floating center card.
          Mounts with the app's shared reveal (`bg-slidein`, like Guides/Sheet).
          `min-h-0` is load-bearing: a flex item's default `min-height:auto` floors
          it at its content (the felt), so a tall bottom sheet used to push the
          table — and the seats pinned around its rim — off the screen. With the
          floor removed, this area shrinks and the felt below scales to fit it. */}
      {/* ⚠️ `py-3`, NOT `py-6`. This box is what the felt is sized against
          (`felt = main − this padding − the 72px seat reserve`), so every pixel here comes
          straight off the DISC on the one axis this screen is short of — and the 72px reserve
          below already does the job the padding looks like it is doing (the seats hang ±36px
          outside the rim, which is the real breathing room). Measured on the operator's device
          geometry — 384×823.5 logical, safeTop 79.6, 3-button nav — seats need felt ≥ 270px to
          clear the centre card and `py-6` left 237.6; this returns 24px of it. */}
      <main
        // `--seat-reserve`: the room the rim seats hang outside the felt, top and bottom (below)
        className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden px-4 py-3 [--seat-reserve:72px] [@media(max-height:440px)]:py-0 [@media(max-height:440px)]:[--seat-reserve:40px]"
        style={{ animation: "bg-slidein 0.25s ease-out" }}
      >
        {/* Full-house celebration — floats in the empty band above the felt so
            the countdown card + the (unchanged) not-full layout stay put; it's
            an absolute overlay (never a flex item) with pointer-events off, so
            it can't shift the centred felt nor intercept a tap. The motion lives
            in `.full-house-banner` (theme.css): a `pop-in-glow` entrance plus a
            soft gold-glow breathe, both disabled under prefers-reduced-motion. */}
        {fullHouse ? (
          <div
            className="pointer-events-none absolute inset-x-0 top-0 z-30 flex justify-center px-4 pt-3"
            style={fitStyle(bannerFit)}
          >
            <div
              ref={bannerRef}
              data-testid="full-house-banner"
              className="full-house-banner flex max-w-[320px] flex-col items-center gap-0.5 rounded-2xl border border-gold-deep/40 bg-surface-container-high/90 px-5 py-2.5 text-center"
              style={{ backdropFilter: "blur(6px)" }}
            >
              <p className="m-0 text-body-lg font-extrabold text-secondary">{t.fullHouseTitle}</p>
              <p className="m-0 text-caption text-on-surface-variant">{t.fullHouseSubtitle}</p>
            </div>
          </div>
        ) : null}

        {/* PUBLIC-TABLE invite affordance: the «+» hint, or my one live invite's pending state.
            An ABSOLUTE overlay, never a flex child — <main>'s only flex item is the felt, whose
            size is `min(82vw, 340px, 100%)` against this box's post-flex height. Adding a sibling
            here would shrink the felt (and drag the rim-pinned seats with it) on a short viewport,
            which is exactly the regression that once clipped the top/bottom seats away entirely.
            Same technique as the full-house banner above.

            `pb-10` lifts the line clear of the felt's lower rim (raised on request). Safe to tune
            HERE precisely BECAUSE it is an absolute overlay: it moves without touching the felt's
            box, the thing that must not shift. The Chromium check asserts it never ends up
            overlapping the felt disc at any viewport. */}
        {showInviteHint || overlayInvite ? (
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0 z-30 flex justify-center px-4 pb-10"
            style={fitStyle(hintFit)}
          >
            {overlayInvite ? (
              <span
                ref={hintRef}
                className="pointer-events-auto flex max-w-full items-center gap-2 rounded-full border border-gold-deep/40 bg-surface-container-high/90 px-3 py-1.5 text-caption text-on-surface-variant shadow-md"
              >
                <span className="min-w-0 truncate">
                  {fa.online.seatInvitePending(overlayInvite.name)}
                </span>
                {/* DISTINCT from the line it sits on: withdrawing an invite is a real action, and
                    an outline-on-outline chip read as part of the sentence. Error-tinted, which is
                    the app's existing vocabulary for a destructive control (the «پاک کردن همه» chip
                    in Messages uses the same pair). */}
                <button
                  type="button"
                  onClick={() => onCancelInvite?.(overlayInvite.id)}
                  className="flex-none cursor-pointer appearance-none rounded-full border border-error/40 bg-error-container/60 px-2.5 py-0.5 text-caption font-bold text-on-error-container transition-colors hover:bg-error-container active:scale-95"
                >
                  {fa.invite.cancel}
                </button>
              </span>
            ) : (
              <span
                ref={hintRef}
                className="max-w-full truncate rounded-full border border-gold-deep/30 bg-surface-container-high/80 px-3 py-1 text-caption text-on-surface-variant shadow-sm"
              >
                {fa.online.seatInviteHintPublic}
              </span>
            )}
          </div>
        ) : null}

        {/* Sized by the SHORTER axis — the felt is a SQUARE whose side is the
            smallest of 82vw / 340px / the height this area actually has left
            (`100%` resolves against <main>'s definite, post-flex height; the width
            then follows the height through `aspect-square`, so the disc can never
            become an ellipse and drag the rim-pinned seats with it).
            It used to be a FIXED 82vw square, which on a 360×590 Telegram viewport
            left <main> just 74px tall: the felt overflowed it by 111px top AND
            bottom and the top/bottom seats were clipped away entirely — the
            "table pushed down under the settings" report. Measured in Chromium
            at 320/360/390/412px: square, inside <main>, seats visible.
            `minHeight` is only a floor for a pathologically short viewport. */}
        <div
          ref={feltRef}
          data-testid="felt"
          className="relative aspect-square"
          // ⚠️ THE SEATS HANG OUTSIDE THE FELT. Each is pinned to the rim with
          // `translate(-50%,-50%)`, so roughly half a seat box (~36px) sticks out top and bottom.
          // Sizing the disc to the FULL available height therefore centred the DISC while the
          // seat ring overflowed — measured: 31px of the top seat above <main>'s edge and the
          // whole group flush against the bottom (19px above / 0 below for the disc itself).
          // Reserving the overhang makes the group — disc AND seats — the thing that gets centred.
          // …at most 40 px on a screen ≤ 440 px tall (B6, the review's round: at 256×393@1.5 the
          // seats hang ~15 px a side, and the 72 left the centre card no room above its floor)
          style={{
            height: "min(82vw, 340px, calc(100% - var(--seat-reserve, 72px)))",
            width: "auto",
            minHeight: 120,
          }}
        >
          {/* Felt disc + radial texture (clipped to the circle). */}
          <div
            className="absolute inset-0 overflow-hidden rounded-full border-8 border-surface-container-high bg-felt-deep"
            style={{
              boxShadow:
                "inset 0 4px 24px rgb(var(--c-felt-shadow) / 0.8), 0 0 40px rgb(var(--c-gold-deep) / 0.1)",
            }}
          >
            <div
              className="absolute inset-0 rounded-full"
              style={{
                background:
                  "radial-gradient(circle at center, rgb(var(--c-felt-edge) / 0.2) 0%, rgb(var(--c-felt-edge) / 0.85) 100%)",
              }}
            />
          </div>

          {/* Seats — the local player at the bottom, the others WHERE THIS GAME'S TABLE WILL DRAW
              THEM, so nobody changes side at the deal (operator, 2026-09-29). */}
          {Array.from({ length: seats }).map((_u, i) => {
            return (
              <Seat
                key={i}
                seatIndex={i}
                angle={tableSeatAngle(gameId, i, seat ?? 0, seats)}
                player={bySeat.get(i)}
                isYou={seat !== null && i === seat}
                onInvite={inviteFor(i)}
              />
            );
          })}

          {/* Center card — the starting countdown, or the stage and the count — scaled to clear every
              seat where it would cover one, never below its floor, shedding its caption row and
              then stepping aside where it cannot (`useCentreCardFit`). The host's start is in it,
              under the headline (card-98 item 7); the lone player's way out stays pinned in the sheet
              below (the B6 review, I1). */}
          <div
            ref={cardRef}
            data-card-scale={cardScale}
            data-card-form={cardForm}
            className={`absolute left-1/2 top-1/2 z-20 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center rounded-xl border border-gold-deep/20 bg-surface-container-high/85 text-center shadow-xl ${
              cardForm === "none" ? "invisible " : ""
            }${
              // the whole card's lines are `nowrap`: its box holds them (`min-w-min`) wherever the
              // old caps are narrower than a line under a text zoom, and the fit scales the whole
              wholeCard ? "min-w-min " : ""
            }${
              // Both boxes size SNUGLY to their content (they used to be pinned to 64% of
              // the felt, which made the waiting card wider than anything in it) — the
              // cap keeps a long game title from stretching it.
              // the caps meet the band the side seats leave (`--card-band`, `useCentreCardFit`);
              // `w-max`, never `w-auto`: an absolute box at `left-1/2` may otherwise shrink to the
              // felt's HALF and wrap its lines there (the B6 review's round — now that they wrap)
              starting
                ? `w-max max-w-[min(170px,var(--card-band,170px))] ${cardCaption ? "px-5 py-2.5" : "px-2 py-1"}`
                : wholeCard
                  ? // the waiting card exactly as before 10-22 (card-98 item 7)
                    "w-auto max-w-[248px] px-3 py-2.5 max-[359px]:max-w-[158px] max-[359px]:px-2"
                  : /* ⚠️ THE `max-[359px]` CAP IS THE STRUCTURAL HALF, AND IT IS NOT REDUNDANT
                     WITH THE BUTTON'S OWN TIGHTENING. At 320 the felt is 82vw = 262px and the
                     two side seats leave a 162px gap; the card is `w-auto`, so it hugs its
                     widest child — and `truncate` does NOT shrink a max-content width, so a
                     longer game title would push the card back over that gap even with the
                     button trimmed. The cap makes «never wider than the gap» true by
                     construction; the trimming is what keeps the button inside it. */
                    `w-max max-w-[min(248px,var(--card-band,248px))] max-[359px]:max-w-[min(158px,var(--card-band,158px))] ${
                      // the tight form sheds its caption row AND its padding's air
                      cardCaption ? "px-3 py-2.5 max-[359px]:px-2" : "px-2 py-1"
                    }`
            }`}
            style={{
              backdropFilter: "blur(6px)",
              ...(cardBand !== null ? { ["--card-band" as string]: `${cardBand}px` } : {}),
              // the class's own centring, then the fit — about the card's centre, the felt's
              ...(cardScale < 1 ? { transform: `translate(-50%, -50%) scale(${cardScale})` } : {}),
            }}
          >
            {starting ? (
              <>
                <div
                  className="flex h-11 w-11 animate-pulse items-center justify-center rounded-full text-headline-sm font-extrabold text-ink-on-gold"
                  style={{
                    background:
                      "linear-gradient(120deg,rgb(var(--c-secondary)),rgb(var(--c-gold-deep)))",
                    boxShadow: "0 0 20px rgb(var(--c-secondary) / .5)",
                  }}
                >
                  {faDigits(countdown)}
                </div>
                {cardCaption ? (
                  <p className="mt-1.5 text-caption font-bold text-secondary">
                    {fa.online.starting}
                  </p>
                ) : null}
              </>
            ) : nothingCounting ? (
              /* A PUBLIC table with no clock running — the state the fill gate created. It must
                 read as HELD, never as a hang: say what is being waited for, show the frozen
                 number when there is one, and offer the way out (solo is where the bots live). */
              <>
                {/* The game's own name, exactly as the ordinary waiting card carries it — this
                    branch REPLACES that card for a lone player, so dropping the caption would
                    take the game name off the screen with it. */}
                {cardCaption ? (
                  <span className="mb-0.5 max-w-full truncate text-caption text-on-surface-variant">
                    {title}
                  </span>
                ) : null}
                {/* The parked number rides the headline's own row as a chip, not a disc above it.
                    MEASURED at 320×568: the felt is 262px and this card is centred in it, so every
                    row costs clearance at the bottom seat — with the disc on its own row the card
                    reached 219px and covered that seat's avatar. Same information, one row. */}
                <div className="flex items-center gap-1.5">
                  {countdownHeld > 0 ? (
                    <span
                      data-testid="countdown-held"
                      /* The running countdown's gold, DIMMED and NOT pulsing — the same number,
                         visibly parked. A pulsing number that never changes is what reads frozen. */
                      className="rounded-full px-1.5 py-0.5 text-label-md font-extrabold text-ink-on-gold opacity-45"
                      style={{
                        background:
                          "linear-gradient(120deg,rgb(var(--c-secondary)),rgb(var(--c-gold-deep)))",
                      }}
                    >
                      {faDigits(countdownHeld)}
                    </span>
                  ) : null}
                  {/* wraps inside the card's cap rather than overflowing it (B6) */}
                  <h2 className="text-headline-sm text-on-surface">{fa.online.waitingForReal}</h2>
                </div>
                {/* NO seated-count row here, unlike the ordinary waiting card: the seat RING shows
                    who is seated far more directly than «۱/۴» does. The hint and the way out
                    («بازی تک‌نفره») live in the sheet below since B6 (the review, I1): no control in
                    a card that may be drawn smaller. */}
              </>
            ) : teamSelect ? (
              /* THE STAGE NAME, AND NOTHING ELSE. This card is centred in the felt and the felt
                 is the narrow axis (`min(82vw, 340px, 100% - 72px)`), so an absolutely-positioned
                 card here is TALL rather than wide — MEASURED at 320 / 360×640 / 440×640 the old
                 three-child card was 97.7 × 215.9px, of which the hint alone wrapped to FIVE lines
                 (96px, 44% of the card), and it covered the top seat and «شما»'s avatar.

                 Both dropped children were REDUNDANT, not merely expensive:
                   • the game title is already on the screen the player just came from, and this
                     card only ever appears mid-table where they know what they are in;
                   • the hint is rendered VERBATIM by the sheet's own panel below (host: the
                     swap instruction; guest: «میزبان در حال چیدن…» beside a spinner), which is
                     where it has the width to be one or two lines instead of five.
                 Card is 73.9px now, and the seats are clear at every viewport except 320×568.
                 ⚠️ Keep the sheet's copies — dropping BOTH would take the instruction off screen.
                 BELOW 360 px THE TITLE IS ONE LINE AT `body-lg` (card-a7): under text ×1.3 the
                 headline wrapped inside the card's 158 px and the card reached the side seats; one
                 smaller line is narrower than the gap the seats leave (`TEAMS_FELT_MIN`). Wider
                 screens keep the headline as it was — one line there reaches the side names at ×1.5. */
              <h2 className="text-headline-sm text-on-surface max-[359px]:whitespace-nowrap max-[359px]:text-body-lg">
                {arrangesColors ? t.colorsTitle : t.teamsTitle}
              </h2>
            ) : (
              <>
                {/* THE GAME'S NAME AND THE SEAT COUNT SHARE A ROW — MEASURED, not tidied.
                    On the felt the card is centred and the seats are pinned to the RIM, so
                    every row it carries costs clearance at the top seat. The count used to sit
                    on its own row at `text-numeral-xl` with `my-1`, which is 40px of the card's
                    162 — and at 320×568 that put the card's top edge at t 165 against the top
                    seat's «منتظر…» label ending at t 173. Same information, one row, and the
                    seat RING itself already says who is seated far more directly than «۱/۴»
                    does. Exactly the fold the `nothingCounting` branch above made, for exactly
                    the same reason.
                    ⚠️ IT MUST NOT WIDEN THE CARD: the title truncates and the chip is
                    `flex-none`, so this row is 128px against the headline's 134 and the card's
                    width is still set by the button below. */}
                {cardCaption ? (
                  <div className="mb-0.5 flex max-w-full items-center gap-1.5">
                    <span className="min-w-0 truncate text-caption text-on-surface-variant">
                      {title}
                    </span>
                    <span className="flex-none rounded-full bg-surface-container-highest px-1.5 py-0.5 text-label-md tabular-nums text-secondary">
                      {`${faDigits(joined)}/${faDigits(seats)}`}
                    </span>
                  </div>
                ) : null}
                {/* ONE LINE, always — as before 10-22 (card-98 item 7: the card is whole and scales
                    instead of wrapping). The card is `w-auto`, so it hugs its widest child — the
                    start BUTTON (143px) left this headline (135px with the real Vazirmatn) just 8px
                    of slack, and on Android it tipped over and wrapped; `nowrap` makes its
                    max-content width the card's floor instead. */}
                <h2 className="whitespace-nowrap text-headline-sm text-on-surface">
                  {t.waitingForPlayers}
                </h2>
                {/* THE HOST'S START IS BACK IN THE CARD, UNDER ITS LAST LINE (operator, 2026-10-22:
                    «back into the table's centre card, under its last line of text — at the bottom it
                    covers the friends list on private tables, so on Android the last friend can't be
                    added»). Restored as it stood before card-a7 B6's review (857d441b): the B6 strip
                    pinned at the sheet's bottom is gone. */}
                {isHost ? (
                  <button
                    type="button"
                    // The bot-fill start declares itself (see StartIntent): the server
                    // deals right away and never stages teams for this press.
                    onClick={() => {
                      // ISSUE 6 — board games have no cards; give them the gong, not a deal.
                      playSfx(GAME_CATEGORY[gameId as GameId] === "board" ? "boardStart" : "deal");
                      onStart?.(botStart ? { withBots: true } : undefined);
                    }}
                    /* `whitespace-nowrap`: «با ربات‌ها شروع کن» + the robot icon wrapped onto
                       TWO lines inside the centre card once it was sized to its content.
                       ⚠️ AND THIS BUTTON IS WHAT SETS THE CARD'S WIDTH, WHICH IS THE OTHER HALF
                       OF THE OVERLAP. At 320×568 the felt is 82vw = 262px and the two side
                       seats' dashed circles leave a 162px gap between them; the button was
                       161px, so the card was 185px and ate 12px of each circle. `max-[359px]:`
                       tightens the padding and the gap on the ONE width where the gap is
                       narrower than the card — and drops the robot glyph there, which is the
                       only thing on it that the LABEL does not already say («با ربات‌ها شروع
                       کن» is literally «start with the bots»). Above 360 nothing changes. */
                    className="flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-primary-fixed-dim/30 bg-primary-container px-3 py-1.5 text-caption font-bold text-on-primary-container transition-transform hover:bg-primary-container/80 active:scale-95 max-[359px]:gap-1 max-[359px]:px-2"
                    style={{ boxShadow: "0 0 16px rgba(232,194,104,0.25)" }}
                  >
                    {/* The robot belongs on the bot-fill start ONLY — a full human table
                        starts with «بزن بریم!», and no robot is joining it. */}
                    {botStart ? (
                      <span className="flex-none max-[359px]:hidden">
                        <Icon name="robot" size={14} />
                      </span>
                    ) : null}
                    {startLabel}
                  </button>
                ) : null}
              </>
            )}
          </div>
        </div>
      </main>

      {/* Bottom sheet — the invite panel (a private table's host inline; a guest waits for the
          host's start and reaches the same panel from one button), or the arrangement stage.
          ⚠️ A PUBLIC table shows NO sheet unless it is ARRANGING (منچ's colour stage lives in it).
          Two reasons, and both matter: its quick-match line promises «اگه میز پر نشه، با ربات‌ها
          شروع می‌کنیم», which is a lie while nothing is counting; and the sheet is a flex sibling
          of the felt, so letting it appear shrinks the table and drags the rim-pinned seats with
          it — the regression that once clipped the top and bottom seats off a short screen.
          SINCE B6 THE LONE PLAYER'S STATE HAS ONE TOO (the review, I1: no control in a card that may
          be drawn smaller): its hint and «بازی تک‌نفره», pinned — never the quick-match line; and the
          sheet's cap yields to the felt like every stage's (`TEAMS_SHEET_MAX_H`). */}
      {starting ? null : (
        <div
          className="flex-none border-t border-outline-variant bg-surface-container-high"
          style={{
            boxShadow: "0 -8px 24px rgba(0,0,0,0.6)",
            paddingBottom:
              "calc(16px + max(env(safe-area-inset-bottom), var(--tg-safe-bottom, 0px)))",
          }}
        >
          <div
            className="mx-auto max-w-[640px] overflow-y-auto overflow-x-hidden overscroll-contain px-md pt-md"
            // The sheet may never eat the table: it takes AT MOST ~46% of the app
            // height and scrolls inside that. The game-info + invite blocks had
            // grown enough to push the felt (and the seats around it) off-screen.
            style={{
              // EVERY stage's sheet yields to the felt (`TEAMS_SHEET_MAX_H`): the partnership stage
              // since card-a7 B4, and since B6 a private table's waiting stage (the host's, a
              // guest's) and منچ's colours — on a short screen the plain 46 % cap left the felt at
              // its 120 px floor and the seats on one another; below the felt's need it scrolls
              // (B4's partnership stage keeps its own 240 px need; منچ's colours take the waiting's)
              maxHeight: teamSelect && !arrangesColors ? TEAMS_SHEET_MAX_H : WAITING_SHEET_MAX_H,
            }}
          >
            {teamSelect ? (
              /* Arrangement stage — the host moves players, guests watch. منچ assigns
                 COLOURS (a seat is a corner of the board); حکم/شلم assign partnerships. */
              arrangesColors ? (
                <ColorArrangePanel
                  players={players}
                  seats={seats}
                  selfSeat={seat}
                  isHost={isHost}
                  countdown={countdown}
                  onSetTeams={onSetTeams}
                  onStart={() => onStart?.()}
                />
              ) : (
                <TeamArrangePanel
                  players={players}
                  seats={seats}
                  selfSeat={seat}
                  isHost={isHost}
                  onSetTeams={onSetTeams}
                  onStart={() => onStart?.()}
                />
              )
            ) : general ? (
              /* THE LONE PLAYER (a public table, nothing counting): what is being waited for is the
                 card's; why, and the way out, are here — the way out pinned like a stage's confirm */
              <div className="flex flex-col items-center gap-2">
                <p className="m-0 text-center text-caption leading-snug text-on-surface-variant">
                  {countdownHeld > 0 ? fa.online.countdownHeldHint : fa.online.waitingForRealHint}
                </p>
                {onPlaySolo ? (
                  <div
                    data-testid="solo-strip"
                    className="sticky bottom-0 z-10 -mx-md flex justify-center self-stretch bg-surface-container-high px-md"
                  >
                    <button
                      type="button"
                      onClick={onPlaySolo}
                      className="flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-primary-fixed-dim/30 bg-primary-container px-3 py-1.5 text-caption font-bold text-on-primary-container transition-transform hover:bg-primary-container/80 active:scale-95"
                      style={{ boxShadow: "0 0 16px rgba(232,194,104,0.25)" }}
                    >
                      <Icon name="robot" size={14} />
                      {fa.online.playSoloInstead}
                    </button>
                  </div>
                ) : null}
              </div>
            ) : isHost ? (
              <div className="flex flex-col gap-2">
                {settings ? <SettingsChips settings={settings} game={gameId} /> : null}
                <InvitePanel
                  gameId={gameId}
                  roomId={roomId}
                  seatedUserIds={seatedUserIds}
                  copyState={copyState}
                  onCopy={copy}
                  onShare={share}
                  consumePendingInvite
                />
                {/* The start lives in the centre card again (card-98 item 7) — nothing is pinned
                    over the friend list. */}
              </div>
            ) : (
              /* A GUEST: the table's rules, who they are waiting for (the start is still the host's),
                 and — card-47 — ONE «دعوت دوستان» button that opens the host's SAME invite panel in
                 the shared sheet. Not the panel inline: this sheet is a flex sibling of the felt,
                 and MEASURED it must stay no taller than master's (see `GuestInvite`) — which is
                 also why the rules ride the host's compact ICON row (`SettingsChips`). */
              <div className="flex flex-col gap-2">
                {settings ? <SettingsChips settings={settings} game={gameId} /> : null}
                <div className="flex items-center justify-center gap-2 text-body-md font-bold text-on-surface-variant">
                  <Spinner />
                  {fa.online.waitingForHost}
                </div>
                <GuestInvite
                  gameId={gameId}
                  roomId={roomId}
                  seatedUserIds={seatedUserIds}
                  copyState={copyState}
                  onCopy={copy}
                  onShare={share}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
